import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { profileService, favouritesService, reviewsService } from '../../services/supabaseService';
import { MessageCircle, Heart, Check, ArrowLeft, Star } from 'lucide-react';

const ProviderProfileViewPage = () => {
  const { userId } = useParams();
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [providerProfile, setProviderProfile] = useState(null);
  const [isFav, setIsFav] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [hasReviewed, setHasReviewed] = useState(false);
  const [loading, setLoading] = useState(true);

  const isPremium = ['premium', 'active', 'trialing'].includes(
    String(userProfile?.subscriptionStatus ?? userProfile?.subscription_status ?? 'free').toLowerCase()
  );
  const isProvider = userProfile?.role === 'service_provider';
  const canMessageOrFav = isPremium || isProvider;

  useEffect(() => {
    if (!userId || !user?.id) return;
    Promise.all([
      profileService.getProfile(userId),
      profileService.getProviderProfile(userId),
      favouritesService.isFavourite(user.id, userId),
      reviewsService.listForProvider(userId),
    ])
      .then(([p, pp, fav, revs]) => {
        setProfile(p);
        setProviderProfile(pp);
        setIsFav(!!fav);
        setReviews(revs || []);
        setHasReviewed((revs || []).some((r) => r.reviewer_id === user?.id));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId, user?.id]);

  const toggleFavourite = async () => {
    if (!canMessageOrFav) {
      navigate('/payment');
      return;
    }
    if (!user?.id || !userId) return;
    try {
      if (isFav) {
        await favouritesService.remove(user.id, userId);
        setIsFav(false);
      } else {
        await favouritesService.add(user.id, userId);
        setIsFav(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const goToChat = () => {
    if (!canMessageOrFav) {
      navigate('/payment');
      return;
    }
    navigate(`/dashboard/chats?with=${userId}`);
  };

  const submitReview = async () => {
    if (!user?.id || userId === user.id || hasReviewed || submittingReview) return;
    setSubmittingReview(true);
    try {
      await reviewsService.add(user.id, userId, reviewRating, reviewComment.trim() || null);
      const revs = await reviewsService.listForProvider(userId);
      setReviews(revs);
      setHasReviewed(true);
      setReviewComment('');
    } catch (err) {
      console.error(err);
    }
    setSubmittingReview(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12 text-slate-400">
        <p>Profile not found.</p>
        <Link to="/dashboard/find" className="text-brand-pink font-medium mt-2 inline-block hover:underline">
          Back to Find a Friend
        </Link>
      </div>
    );
  }

  const hourlyRate = providerProfile?.hourly_rate_paise
    ? `₹${(providerProfile.hourly_rate_paise / 100).toLocaleString()}/hr`
    : null;
  const categories = providerProfile?.categories || [];

  return (
    <div>
      <Link
        to="/dashboard/find"
        className="inline-flex items-center gap-2 text-slate-400 hover:text-brand-pink mb-6 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Find a Friend
      </Link>

      <div className="rounded-2xl overflow-hidden bg-slate-800/50 border border-white/10 max-w-2xl">
        <div className="aspect-[3/2] bg-slate-700 relative overflow-hidden">
          <img
            src={profile.avatar_url || '/default-avatar.svg'}
            alt={profile.display_name || 'Profile'}
            className="w-full h-full object-cover"
          />
          {profile.is_verified && (
            <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-brand-pink flex items-center justify-center">
              <Check className="h-5 w-5 text-white" />
            </div>
          )}
        </div>
        <div className="p-6">
          <h1 className="font-heading font-bold text-2xl text-white">
            {profile.display_name || profile.username || 'Friend'}
          </h1>
          {(profile.city || hourlyRate) && (
            <p className="text-slate-400 mt-1">
              {[profile.city, hourlyRate].filter(Boolean).join(' · ')}
            </p>
          )}
          {profile.bio && (
            <p className="mt-4 text-slate-300">{profile.bio}</p>
          )}
          {categories.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((c) => (
                <span
                  key={c}
                  className="px-3 py-1 rounded-full bg-brand-pink/20 text-brand-pink text-sm font-medium"
                >
                  {c === 'hire_a_friend' ? 'Hire a friend' : c === 'fwb' ? 'FWB' : c}
                </span>
              ))}
            </div>
          )}
          {providerProfile?.do_dont_text && (
            <div className="mt-4 p-4 rounded-xl bg-slate-900/50 border border-white/10">
              <h3 className="text-sm font-semibold text-slate-200 mb-2">Do&apos;s and Don&apos;ts</h3>
              <p className="text-slate-400 text-sm whitespace-pre-wrap">{providerProfile.do_dont_text}</p>
            </div>
          )}
          {reviews.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-slate-200 mb-2">Reviews</h3>
              <div className="space-y-3">
                {reviews.slice(0, 10).map((r) => (
                  <div key={r.id} className="p-3 rounded-xl bg-slate-900/50 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-white">
                        {r.user_profiles?.display_name || r.user_profiles?.username || 'User'}
                      </span>
                      <span className="flex items-center gap-0.5 text-amber-500">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} className={`h-4 w-4 ${i <= r.rating ? 'fill-current' : ''}`} />
                        ))}
                      </span>
                    </div>
                    {r.comment && <p className="text-sm text-slate-400">{r.comment}</p>}
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(r.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {user?.id && userId !== user.id && !hasReviewed && (
            <div className="mt-6 p-4 rounded-xl border border-white/10 bg-slate-900/30">
              <h3 className="text-sm font-semibold text-slate-200 mb-2">Leave a review</h3>
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setReviewRating(i)}
                    className="p-1 text-amber-500 hover:scale-110 transition-transform"
                    aria-label={`${i} star`}
                  >
                    <Star className={`h-6 w-6 ${i <= reviewRating ? 'fill-current' : ''}`} />
                  </button>
                ))}
              </div>
              <textarea
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Optional comment..."
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-white/20 bg-slate-800 text-slate-200 text-sm mb-2 placeholder:text-slate-500"
              />
              <button
                type="button"
                onClick={submitReview}
                disabled={submittingReview}
                className="px-4 py-2 rounded-lg bg-brand-pink text-white text-sm font-medium hover:bg-brand-pink-dark disabled:opacity-50"
              >
                {submittingReview ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          )}
          {!canMessageOrFav && (
            <div className="mt-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm">
              Subscribe to message this friend or add them to favourites.
              <Link to="/payment" className="ml-2 font-medium underline hover:text-amber-100">
                Upgrade now
              </Link>
            </div>
          )}
          <div className="mt-6 flex gap-3">
            {canMessageOrFav ? (
              <>
                <button
                  type="button"
                  onClick={goToChat}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-brand-pink text-white font-semibold hover:bg-brand-pink-dark transition-colors"
                >
                  <MessageCircle className="h-5 w-5" />
                  Message
                </button>
                <button
                  type="button"
                  onClick={toggleFavourite}
                  className={`flex items-center justify-center gap-2 py-3 px-6 rounded-xl border-2 transition-colors ${
                    isFav
                      ? 'border-brand-pink bg-brand-pink/20 text-brand-pink'
                      : 'border-white/20 text-slate-400 hover:bg-white/10'
                  }`}
                  aria-label={isFav ? 'Remove from favourites' : 'Add to favourites'}
                >
                  <Heart className={`h-5 w-5 ${isFav ? 'fill-current' : ''}`} />
                  {isFav ? 'Saved' : 'Save'}
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => navigate('/payment')}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600"
              >
                Upgrade to message & save
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderProfileViewPage;
